import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  Building2, Users, MapPin, Award, Briefcase, Heart,
  Sparkles, Target, Compass, Globe, Code2, Megaphone,
  Coffee, GraduationCap, Plane, Trophy, ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Block = { icon: LucideIcon; title: string; body: string };
type ChildPage = {
  title: string;
  eyebrow: string;
  sub: string;
  metaDesc: string;
  intro: string;
  blocks: Block[];
  cta?: { label: string; to: string };
};

const CHILDREN: Record<string, ChildPage> = {
  "about-us": {
    eyebrow: "About",
    title: "About Us",
    sub: "Engineers, designers and operators building real products.",
    metaDesc: "CoderLity is a global digital agency of engineers, designers and operators delivering software, growth and infrastructure.",
    intro:
      "CoderLity started in 2017 as a small team of engineers helping founders ship faster. Eight years later we run product, infrastructure and growth for ambitious teams across 28 countries — staying small enough to care, big enough to deliver enterprise-grade work.",
    blocks: [
      { icon: Compass, title: "Our mission", body: "Help builders ship the right thing, faster — without compromising on craft." },
      { icon: Target, title: "How we work", body: "Senior teams, weekly demos, fixed-scope sprints with transparent pricing." },
      { icon: Sparkles, title: "What we value", body: "Craft, candor, ownership and outcomes. We sweat the details so you don't." },
      { icon: Globe, title: "Global by default", body: "Riyadh, Jeddah, Rangpur and remote — follow-the-sun coverage built in." },
    ],
  },
  "our-team": {
    eyebrow: "About · People",
    title: "Our Team",
    sub: "Senior specialists who've shipped at scale.",
    metaDesc: "Meet the CoderLity team — engineers, designers, strategists and operators with decades of shipping experience.",
    intro:
      "60+ people across product engineering, design, DevOps, growth, content and ops. Average tenure in the field is 9 years — most of our team has shipped at startups and enterprises before joining.",
    blocks: [
      { icon: Code2, title: "Engineering", body: "Full-stack, mobile, DevOps and ML — across React, Node, Python, Swift and Kotlin." },
      { icon: Sparkles, title: "Design", body: "Brand, product and motion designers obsessed with details and accessibility." },
      { icon: Megaphone, title: "Growth", body: "Performance marketing, SEO, content, lifecycle and CRO specialists." },
      { icon: Users, title: "Leadership", body: "Product, delivery and account leads keeping every engagement on track." },
    ],
  },
  "branches": {
    eyebrow: "About · Presence",
    title: "Branches",
    sub: "Four offices, one team, follow-the-sun coverage.",
    metaDesc: "CoderLity offices in Riyadh, Jeddah and Rangpur plus a global remote team for around-the-clock delivery.",
    intro:
      "Our offices are placed for talent, time-zone coverage and client proximity. Same shared playbook, same delivery standards — wherever your team works with us.",
    blocks: [
      { icon: MapPin, title: "Riyadh, KSA", body: "HQ — enterprise sales, delivery leadership and client services." },
      { icon: MapPin, title: "Jeddah, KSA", body: "Account management, client success and on-the-ground support." },
      { icon: MapPin, title: "Rangpur, BD", body: "Engineering and design studio — the production heart of CoderLity." },
      { icon: Globe, title: "Remote · 4 continents", body: "Specialists who plug in fractionally where it makes most sense." },
    ],
  },
  "awards": {
    eyebrow: "About · Recognition",
    title: "Awards",
    sub: "Recognized for craft, impact and culture.",
    metaDesc: "Awards and recognition for CoderLity's work in design, engineering, growth and workplace culture.",
    intro:
      "We don't chase awards — but when peers, clients and industry bodies recognize the work, we're proud to share it.",
    blocks: [
      { icon: Trophy, title: "Awwwards · SOTD", body: "Multiple Site of the Day and Honorable Mentions for client launches." },
      { icon: Trophy, title: "Clutch Top B2B", body: "Recognized in Clutch's top web and software developers list for MENA." },
      { icon: Trophy, title: "G2 High Performer", body: "Internal SaaS products consistently rated High Performer on G2." },
      { icon: Award, title: "Great Place to Work", body: "Certified across our Riyadh, Jeddah and Rangpur offices." },
    ],
  },
  "open-positions": {
    eyebrow: "Careers",
    title: "Open Positions",
    sub: "We're hiring senior product, engineering and growth talent.",
    metaDesc: "Open roles at CoderLity — senior engineering, design, DevOps, growth and operations positions across our offices and remote.",
    intro:
      "We hire for craft, ownership and curiosity. Most roles are open in Riyadh, Jeddah and Rangpur, with select positions fully remote. Strong portfolios beat strong CVs every time.",
    blocks: [
      { icon: Code2, title: "Senior Full-stack", body: "React + Node, TypeScript-first. Riyadh / Rangpur / Remote." },
      { icon: Sparkles, title: "Product Designer", body: "Product-led design with motion and prototyping chops. Hybrid." },
      { icon: Briefcase, title: "Delivery Lead", body: "Run multi-stream engagements end-to-end. Riyadh." },
      { icon: Megaphone, title: "Performance Marketer", body: "Paid + SEO + lifecycle for B2B SaaS. Remote." },
    ],
    cta: { label: "Apply via Contact", to: "/contact" },
  },
  "life-at-coderlity": {
    eyebrow: "Careers · Culture",
    title: "Life at CoderLity",
    sub: "Senior teams, calm process, real ownership.",
    metaDesc: "Life at CoderLity — calm async-first culture, learning budgets, generous leave and real ownership of the work.",
    intro:
      "We're an async-first team that protects deep work. Meetings are short, demos are weekly, and ownership is real. Learning budgets, generous leave, and an annual team offsite keep the bar high without the burnout.",
    blocks: [
      { icon: Coffee, title: "Async-first", body: "Few meetings, lots of writing. We default to written decisions and recorded updates." },
      { icon: GraduationCap, title: "Learning budgets", body: "Annual budget per person for courses, conferences and books." },
      { icon: Plane, title: "Annual offsite", body: "Whole team meets in person once a year — Maldives, Bali, Cappadocia past trips." },
      { icon: Heart, title: "Real ownership", body: "Lead projects end-to-end with the support of senior peers." },
    ],
  },
};

export const Route = createFileRoute("/about/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/about/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: AboutChild,
});

function AboutChild() {
  const { item } = Route.useLoaderData();
  return (
    <PageShell>
      <PageHero eyebrow={item.eyebrow} title={item.title} sub={item.sub} />
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{item.intro}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {item.blocks.map((b: Block) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
      {item.cta && (
        <section className="py-12">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <Link
              to={item.cta.to}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow"
            >
              {item.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}
      <CTABanner />
    </PageShell>
  );
}
